import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

type PagePreloaderVariant = "list" | "detail" | "form";

interface PagePreloaderProps {
  variant?: PagePreloaderVariant;
  showAction?: boolean;
  tabCount?: number;
  rowCount?: number;
}

function PageHeaderSkeleton({ showAction = true }: { showAction?: boolean }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-5">
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-72" />
      </div>
      {showAction && <Skeleton className="h-9 w-32" />}
    </div>
  );
}

function TabsSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-28" />
      ))}
    </div>
  );
}

function TableSkeleton({ rowCount = 5 }: { rowCount?: number }) {
  return (
    <div className="space-y-4">
      {/* Table Header */}
      <div className="flex items-center justify-between">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-9 w-24" />
      </div>

      {/* Table Rows */}
      <div className="space-y-3">
        {/* Header Row */}
        <div className="flex items-center gap-4 py-3 border-b">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-4 w-32 flex-1" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-16" />
        </div>

        {/* Data Rows */}
        {Array.from({ length: rowCount }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 py-3">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-10 w-10 rounded-lg" />
            <Skeleton className="h-4 w-40 flex-1" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-4 w-8" />
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between pt-4">
        <Skeleton className="h-4 w-32" />
        <div className="flex gap-2">
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-8" />
        </div>
      </div>
    </div>
  );
}

function DetailContentSkeleton() {
  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="space-y-2">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-4 w-64" />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Left Column - Details */}
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="py-2 border-b border-border space-y-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-4 w-48" />
            </div>
          ))}
        </div>

        {/* Right Column - Charts/Stats */}
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6 space-y-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

function FormContentSkeleton() {
  return (
    <div className="space-y-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-48" />
          <Skeleton className="h-10 w-full" />
        </div>
      ))}

      <div className="flex justify-end gap-2 pt-4">
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-10 w-24" />
      </div>
    </div>
  );
}

export function PagePreloader({
  variant = "list",
  showAction = true,
  tabCount = 4,
  rowCount = 5,
}: PagePreloaderProps) {
  if (variant === "list") {
    return (
      <div className="space-y-6">
        <PageHeaderSkeleton showAction={showAction} />
        <Card>
          <CardContent className="pt-6">
            <TableSkeleton rowCount={rowCount} />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (variant === "detail") {
    return (
      <div className="space-y-6">
        <PageHeaderSkeleton showAction={showAction} />
        <TabsSkeleton count={tabCount} />
        <Card className="mt-5">
          <CardContent className="pt-6">
            <DetailContentSkeleton />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (variant === "form") {
    return (
      <div className="space-y-6">
        <PageHeaderSkeleton showAction={false} />
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent>
            <FormContentSkeleton />
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
